import { CartUpsellingItemSection } from '@/ui/components/layout/cart/CartAddOnServices/CartUpsellingItemSection/types/CartUpsellingItemSection';
import { CartUpsellingItem } from '@/ui/components/layout/cart/CartAddOnServices/CartUpsellingItemSection/types/CartUpsellingItem';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { TileFragmentFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { SmartlockCrystallizeData } from '../types/SmartlockCrystallizeData';

type CartUpsellingData = SmartlockCrystallizeData['cartExtraServices'];

export function parseCartUpsellingContent(
  cartUpsellingData: CartUpsellingData,
): CartUpsellingItemSection[] | undefined {
  const chunks = componentByTypename(cartUpsellingData?.content, 'ContentChunkContent')?.chunks;

  // loop through all chunks - each chunk with description and upselling item
  return chunks?.flatMap((chunk): CartUpsellingItemSection[] => {
    const sectionDescription =
      componentByTypename(chunk.find((item) => item.id === 'text')?.content, 'RichTextContent') ||
      undefined;
    const cartUpsellingRelatedItems = componentByTypename(
      chunk.find((item) => item.id === 'additional-service')?.content,
      'ItemRelationsContent',
    )?.items;

    const additionalInformationText =
      componentByTypename(
        chunk.find((item) => item.id === 'additional-information-box')?.content,
        'RichTextContent',
      ) || undefined;

    const designVariant =
      componentByTypename(chunk.find((item) => item.id === 'variant')?.content, 'SelectionContent')
        ?.options?.[0]?.value || 'primary';

    const additionalInformationTiles = componentByTypename(
      chunk.find((item) => item.id === 'additional-information-tiles')?.content,
      'ItemRelationsContent',
    )?.items as TileFragmentFragment[];

    const items: CartUpsellingItemSection[] =
      cartUpsellingRelatedItems
        ?.map((item): CartUpsellingItemSection | undefined => {
          if (item.__typename !== 'Product' || !item.variants?.[0]) {
            // if related item is not type of product or product with no variants
            return undefined;
          }

          // pick default variant or the first one (currently there is always one variant for cart
          // upselling)
          const firstVariant =
            item.variants.find((variant) => variant.isDefault) || item.variants[0];

          // If variant also contains a correctly setup subscription plan, adds the information to the service option
          // (current Crystallize setup always uses only 1 subscription plan with 1 period per variant)
          let subscriptionData;
          const subscriptionPlan = firstVariant.subscriptionPlans?.[0];
          const subscriptionPeriod = subscriptionPlan?.periods?.[0];
          if (subscriptionPlan && subscriptionPeriod) {
            subscriptionData = {
              planId: subscriptionPlan.identifier,
              planPeriod: subscriptionPeriod,
            };
          }
          const serviceOption: CartUpsellingItem = {
            id: item.id,
            shape: item.shape.identifier,
            sku: firstVariant.sku!,
            name: item.name!,
            path: item.path!,
            priceVariant: firstVariant.priceVariant || undefined,
            description:
              componentByTypename(item.shortDescription?.content, 'RichTextContent') || undefined,
            image: firstVariant.firstImage || undefined,
            subscription: subscriptionData,
          };

          return {
            description: sectionDescription,
            item: serviceOption,
            additionalInformationText,
            additionalInformationTiles,
            designVariant,
          };
        })
        .filter((section): section is CartUpsellingItemSection => !!section) ?? [];
    // filter empty sections
    return items;
  });
}
